import { TopicCard } from "@/components/TopicCard";
import { TelegramCTA } from "@/components/TelegramCTA";
import { TrendingStrip } from "@/components/TrendingStrip";
import { Search, Radar, Loader2, ChevronRight, Flame, Layers, Eye, Swords } from "lucide-react";
import { useArchive } from "@/hooks/useArchive";

const CATEGORIES = ["All", "Politics", "Science", "Technology", "Health", "Culture", "Economics", "Environment", "History"];

const HeroDiscover = ({ query, setQuery, discovering, onDiscover }) => (
  <section className="relative overflow-hidden border-b border-[#333]">
    <div
      className="absolute inset-0 bg-cover bg-center opacity-30"
      style={{ backgroundImage: "url(https://images.unsplash.com/photo-1502265369151-93a2b7be98a4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)" }}
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]/40" />
    <div className="grid-noise pointer-events-none absolute inset-0 opacity-[0.08]" />

    <div className="relative mx-auto max-w-7xl px-6 py-20 md:py-28">
      <div className="meta-label mb-6 flex items-center gap-3 text-[#D92D20]">
        <span className="h-px w-10 bg-[#D92D20]" /> Classified / Open Source Intelligence
      </div>
      <h1 className="font-heading text-5xl tracking-tighter text-white md:text-7xl max-w-4xl">
        The internet's most <span className="text-[#D92D20]">controversial</span> truths, declassified.
      </h1>
      <p className="mt-6 max-w-2xl font-body text-lg text-[#A3A3A3]">
        A living archive of the most divisive debates online. Both sides, the hidden truth the
        noise buries, and real source links — compiled for the curious.
      </p>

      <div className="mt-10 max-w-2xl">
        <div className="meta-label mb-3 flex items-center gap-2 text-[#FBBF24]">
          <Radar className="h-4 w-4" strokeWidth={1.5} /> Run an investigation
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            data-testid="discover-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onDiscover()}
            placeholder="A topic to dig into… or leave blank for trending"
            className="flex-1 panel p-4 font-mono text-sm text-white placeholder:text-[#737373] focus:border-[#D92D20] focus:outline-none focus:ring-1 focus:ring-[#D92D20]"
          />
          <button
            data-testid="discover-btn"
            onClick={onDiscover}
            disabled={discovering}
            className="flex items-center justify-center gap-2 bg-[#D92D20] px-6 py-4 font-mono text-sm uppercase tracking-wider text-white transition-colors hover:bg-[#B91C1C] disabled:opacity-60"
          >
            {discovering ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Digging</>
            ) : (
              <>Surface <ChevronRight className="h-4 w-4" /></>
            )}
          </button>
        </div>
      </div>
    </div>
  </section>
);

const ViewToggle = ({ view, setView }) => {
  const cls = (active) =>
    `flex items-center gap-2 chip px-4 py-2 ${
      active ? "border-[#D92D20] bg-[#D92D20] text-white" : "border-[#333] text-[#A3A3A3] hover:border-[#525252] hover:text-white"}`;
  return (
    <div className="flex gap-2">
      <button data-testid="view-trending" onClick={() => setView("trending")} className={cls(view === "trending")}>
        <Flame className="h-4 w-4" strokeWidth={1.5} /> Trending
      </button>
      <button data-testid="view-all" onClick={() => setView("all")} className={cls(view === "all")}>
        <Layers className="h-4 w-4" strokeWidth={1.5} /> All Time
      </button>
      <button data-testid="view-read" onClick={() => setView("read")} className={cls(view === "read")}>
        <Eye className="h-4 w-4" strokeWidth={1.5} /> Most Read
      </button>
      <button data-testid="view-divisive" onClick={() => setView("divisive")} className={cls(view === "divisive")}>
        <Swords className="h-4 w-4" strokeWidth={1.5} /> Most Divisive
      </button>
      <button data-testid="view-discussed" onClick={() => setView("discussed")} className={cls(view === "discussed")}>
        <Flame className="h-4 w-4" strokeWidth={1.5} /> Hot Debates
      </button>
    </div>
  );
};

const ArchiveControls = ({ category, setCategory, search, setSearch }) => (
  <div className="flex flex-col gap-6 border-b border-[#333] pb-6 lg:flex-row lg:items-center lg:justify-between">
    <div className="flex flex-wrap gap-2">
      {CATEGORIES.map((c) => (
        <button
          key={c}
          data-testid={`filter-${c.toLowerCase()}`}
          onClick={() => setCategory(c)}
          className={`chip px-3 py-1.5 ${
            category === c ? "border-[#D92D20] bg-[#D92D20] text-white" : "border-[#333] text-[#A3A3A3] hover:border-[#525252] hover:text-white"}`}
        >
          {c}
        </button>
      ))}
    </div>
    <div className="relative w-full lg:w-72">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#737373]" strokeWidth={1.5} />
      <input
        data-testid="search-input"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search archive…"
        className="w-full panel py-2.5 pl-10 pr-3 font-mono text-sm text-white placeholder:text-[#737373] focus:border-[#D92D20] focus:outline-none"
      />
    </div>
  </div>
);

const TopicGrid = ({ loading, topics }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 py-12 md:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={`skeleton-${i}`} className="h-56 animate-pulse panel" />
        ))}
      </div>
    );
  }
  if (topics.length === 0) {
    return (
      <div className="py-24 text-center">
        <p className="font-heading text-2xl text-white">The archive is empty.</p>
        <p className="meta-label mt-3 text-[#737373]">Run an investigation above to surface controversies.</p>
      </div>
    );
  }
  return (
    <div className="grid auto-rows-[minmax(220px,auto)] grid-cols-1 gap-4 py-10 md:grid-cols-3">
      {topics.map((t, i) => (
        <TopicCard key={t.id} topic={t} large={i === 0} index={i} />
      ))}
    </div>
  );
};

const VIEW_TITLES = { trending: "Trending This Week", read: "Most Read", divisive: "Most Divisive", discussed: "Hot Debates", all: "The Full Archive" };
const VIEW_SUBTITLES = {
  trending: "Hottest controversies surfaced in the last 7 days · auto-refreshed",
  read: "The dossiers readers open the most",
  divisive: "Where the vote is split closest to 50/50 — the genuinely contested",
  discussed: "The busiest threads — most comments and votes right now",
  all: "Every declassified dossier, ranked by controversy",
};

export default function Archive() {
  const {
    topics, loading, category, setCategory, search, setSearch,
    view, setView, query, setQuery, discovering, discover,
  } = useArchive();

  return (
    <div>
      <HeroDiscover query={query} setQuery={setQuery} discovering={discovering} onDiscover={discover} />
      <TrendingStrip />

      <section className="mx-auto max-w-7xl px-6 py-10">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="font-heading text-3xl tracking-tight text-white">
              {VIEW_TITLES[view]}
            </h2>
            <p className="meta-label mt-2 text-[#737373]">
              {VIEW_SUBTITLES[view]}
            </p>
          </div>
          <ViewToggle view={view} setView={setView} />
        </div>
        <ArchiveControls category={category} setCategory={setCategory} search={search} setSearch={setSearch} />
        <TopicGrid loading={loading} topics={topics} />

        <div className="mt-12 flex justify-center border-t border-[#333] pt-10">
          <TelegramCTA />
        </div>
      </section>
    </div>
  );
}
